import { useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Main as Container } from 'Components/Main';
import { getSystemById, archiveSystem, unarchiveSystem } from 'services/systems-service';
import { PageLoaderShow } from 'Components/PageLoader/Show';
import { Dialog, PageHeader, Button, EditSvg } from 'Components/ui';
import { formatDate as formatDateIntl, LOCALE_MAP } from 'utils/locale-formatting';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--color-brand)';
const BORDER = 'var(--vo-line)';
const DIVIDER = 'var(--vo-cloud)';
const BG = 'var(--vo-mist)';
const TEXT = 'var(--vo-ink-2)';

const ArchiveBtn = styled.button`
  height: 40px;
  background: white;
  color: ${MUTED};
  border: 1.5px solid ${BORDER};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 24px;
  background: white;
  border-radius: 12px;
  padding: 5px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  width: fit-content;
`;

const TabBtn = styled.button`
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 9px;
  background: ${p => p.active ? BLUE : 'transparent'};
  color: ${p => p.active ? 'white' : MUTED};
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: ${p => p.active ? 600 : 500};
  cursor: pointer;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  box-sizing: border-box;
`;


const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
`;

const FieldLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0 0 6px;
`;

const FieldValue = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${TEXT};
`;

const TagsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  grid-column: 1 / -1;
`;

const TagChip = styled.span`
  background: var(--vo-capri-50);
  color: var(--vo-celeste);
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  padding: 3px 12px;
  border-radius: 20px;
`;

const DescriptionField = styled.div`
  grid-column: 1 / -1;
`;

const DescriptionText = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${TEXT};
  line-height: 1.65;
  margin: 0;
`;

const CritBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${p => p.bg};
  color: ${p => p.color};
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
`;

const BoolBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: ${p => p.yes ? 'var(--vo-success-10)' : 'var(--vo-slate-10)'};
  color: ${p => p.yes ? 'var(--vo-success)' : MUTED};
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 20px;
`;

const Avatar = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const AvatarText = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: white;
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SideCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const SideCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${DIVIDER};
`;

const HistoryRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RelatedRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RelatedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: ${BG};
  border-radius: 10px;
`;

const RelatedLabel = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT};
`;

const RelatedCount = styled.span`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: ${BLUE};
`;

const BondsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
`;

const VinculoCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const VinculoHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${DIVIDER};
`;

const VinculoTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;

const VinculoChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const VinculoChip = styled.span`
  display: inline-flex;
  align-items: center;
  background: var(--vo-cobalto-8);
  color: var(--vo-cobalto);
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 20px;
`;

const MonitorSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2"/>
    <line x1="8" y1="21" x2="16" y2="21"/>
    <line x1="12" y1="17" x2="12" y2="21"/>
  </svg>
);

const IconTile = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--vo-celeste), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-bottom: 4px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${DIVIDER};
`;

const SectionTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0;
`;

const ArchiveSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="21 8 21 21 3 21 3 8"/>
    <rect x="1" y="3" width="22" height="5"/>
    <line x1="10" y1="12" x2="14" y2="12"/>
  </svg>
);

const PlusSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/>
    <line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

const getCriticityInfo = (level, t) => {
  const map = {
    '1': { color: 'var(--vo-success)', bg: 'var(--vo-success-10)' },
    '2': { color: 'var(--vo-celeste)', bg: 'var(--vo-info-10)' },
    '3': { color: 'var(--vo-warning)', bg: 'var(--vo-warning-10)' },
    '4': { color: 'var(--vo-sev-high)', bg: 'var(--vo-sev-high-12)' },
    '5': { color: 'var(--vo-danger)', bg: 'var(--vo-danger-10)' },
  };
  const info = map[level] || { color: MUTED, bg: 'var(--vo-slate-10)' };
  const label = t(`mapping:systems.criticality.short.${level}`, { defaultValue: level });
  return { ...info, label: `${label} (${level})` };
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
};

export const SystemsShow = () => {
  const { t, i18n } = useTranslation(['mapping', 'common']);
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const locale = LOCALE_MAP[i18n.language] ?? 'pt-BR';
  const formatDate = (dateStr) => formatDateIntl(dateStr, locale) || '—';

  const { isLoading, error, data: system, refetch } = useQuery(['getSystemById', id], async () => {
    const dataSystem = await getSystemById(id);
    return dataSystem.status === 200 ? dataSystem.data : null;
  });

  const handleArchive = async () => {
    const isArchived = system?.archived;
    const confirmMsg = isArchived
      ? t('mapping:systems.list.modals.unarchiveMessage', { name: system?.name })
      : t('mapping:systems.list.modals.archiveMessage', { name: system?.name });

    const confirmed = await Dialog.confirm({
      title: confirmMsg,
      confirmLabel: isArchived ? t('mapping:systems.show.unarchive') : t('mapping:systems.show.archive'),
    });

    if (!confirmed) return;

    try {
      const res = isArchived ? await unarchiveSystem(id) : await archiveSystem(id);
      if (res.status === 200) {
        Dialog.alert({
          title: t('mapping:systems.toasts.successTitle'),
          message: isArchived ? t('mapping:systems.toasts.unarchived') : t('mapping:systems.toasts.archived'),
          icon: 'success',
        });
        refetch();
      }
    } catch {
      Dialog.alert({ title: t('mapping:systems.show.error'), icon: 'error' });
    }
  };

  if (isLoading) return <Container><div style={{ paddingTop: 32 }}><PageLoaderShow /></div></Container>;
  if (error || !system) return <Container><div style={{ paddingTop: 32 }}>{t('mapping:systems.show.error')}</div></Container>;

  const critInfo = system.criticity ? getCriticityInfo(system.criticity, t) : null;
  const activeAreas = system.areas?.filter(a => !a.archived) ?? [];
  const activeProcesses = system.processes?.filter(p => !p.archived) ?? [];
  const activeAssets = system.assets?.filter(a => !a.archived) ?? [];

  return (
    <Container>
      <PageHeader
        title={system.name}
        segments={[
          { label: t('common:breadcrumbs.mapping') },
          { label: t('mapping:systems.list.title'), route: '/systems' },
          { label: system.name },
        ]}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate('/systems/edit/' + id)} startIcon={<EditSvg />} sx={{ height: '40px', padding: '0 16px' }}>
              {t('mapping:systems.show.edit')}
            </Button>
            <ArchiveBtn onClick={handleArchive}>
              <ArchiveSvg />
              {system.archived ? t('mapping:systems.show.unarchive') : t('mapping:systems.show.archive')}
            </ArchiveBtn>
          </>
        }
      />

      <TabBar>
        <TabBtn active={activeTab === 0} onClick={() => setActiveTab(0)}>
          {t('mapping:systems.show.tabs.details')}
        </TabBtn>
        <TabBtn active={activeTab === 1} onClick={() => setActiveTab(1)}>
          {t('mapping:systems.show.tabs.links')}
        </TabBtn>
      </TabBar>

      {activeTab === 0 && (
        <MainGrid>
          <InfoCard>
            <SectionHead>
              <IconTile><MonitorSvg /></IconTile>
              <SectionTitle>{t('mapping:systems.show.infoSectionTitle')}</SectionTitle>
            </SectionHead>

            <FieldsGrid>
              <div>
                <FieldLabel>{t('mapping:systems.show.fields.criticality')}</FieldLabel>
                {critInfo
                  ? <CritBadge bg={critInfo.bg} color={critInfo.color}>{critInfo.label}</CritBadge>
                  : <FieldValue>-</FieldValue>
                }
              </div>

              <div>
                <FieldLabel>{t('mapping:systems.show.fields.responsible')}</FieldLabel>
                {system.user
                  ? (
                    <UserRow>
                      <Avatar><AvatarText>{getInitials(system.user)}</AvatarText></Avatar>
                      <FieldValue>{system.user}</FieldValue>
                    </UserRow>
                  )
                  : <FieldValue>-</FieldValue>
                }
              </div>

              <div>
                <FieldLabel>{t('mapping:systems.show.fields.saas')}</FieldLabel>
                <BoolBadge yes={system.saas}>
                  {system.saas ? t('common:boolean.yes') : t('common:boolean.no')}
                </BoolBadge>
              </div>

              <div>
                <FieldLabel>{t('mapping:systems.show.fields.developedInHouse')}</FieldLabel>
                <BoolBadge yes={system.developedInHouse}>
                  {system.developedInHouse ? t('common:boolean.yes') : t('common:boolean.no')}
                </BoolBadge>
              </div>

              <div>
                <FieldLabel>{t('mapping:systems.show.fields.database')}</FieldLabel>
                <FieldValue>{system.dataBase || '-'}</FieldValue>
              </div>

              <TagsRow>
                <FieldLabel style={{ width: '100%' }}>{t('mapping:systems.show.fields.tags')}</FieldLabel>
                {system.tags?.length > 0
                  ? system.tags.map(tag => <TagChip key={tag}>{tag}</TagChip>)
                  : <FieldValue>-</FieldValue>
                }
              </TagsRow>

              <DescriptionField>
                <FieldLabel>{t('mapping:systems.show.fields.description')}</FieldLabel>
                <DescriptionText>{system.description || '-'}</DescriptionText>
              </DescriptionField>
            </FieldsGrid>
          </InfoCard>

          <Sidebar>
            <SideCard>
              <SideCardTitle>{t('mapping:systems.show.historySectionTitle')}</SideCardTitle>
              <HistoryRows>
                <div>
                  <FieldLabel>{t('mapping:systems.show.createdAt')}</FieldLabel>
                  <DescriptionText>{formatDate(system.createdAt)}</DescriptionText>
                </div>
                <div>
                  <FieldLabel>{t('mapping:systems.show.updatedAt')}</FieldLabel>
                  <DescriptionText>{formatDate(system.updatedAt)}</DescriptionText>
                </div>
              </HistoryRows>
            </SideCard>

            <SideCard>
              <SideCardTitle>{t('mapping:systems.show.relatedDataTitle')}</SideCardTitle>
              <RelatedRows>
                <RelatedRow>
                  <RelatedLabel>{t('mapping:systems.show.relatedAreas')}</RelatedLabel>
                  <RelatedCount>{activeAreas.length}</RelatedCount>
                </RelatedRow>
                <RelatedRow>
                  <RelatedLabel>{t('mapping:systems.show.relatedProcesses')}</RelatedLabel>
                  <RelatedCount>{activeProcesses.length}</RelatedCount>
                </RelatedRow>
                <RelatedRow>
                  <RelatedLabel>{t('mapping:systems.show.relatedAssets')}</RelatedLabel>
                  <RelatedCount>{activeAssets.length}</RelatedCount>
                </RelatedRow>
              </RelatedRows>
            </SideCard>
          </Sidebar>
        </MainGrid>
      )}

      {activeTab === 1 && (
        <BondsGrid>
          <VinculoCard>
            <VinculoHeader>
              <VinculoTitle>{t('mapping:systems.show.bonds.areas')}</VinculoTitle>
              <Button iconOnly variant="icon-filled" size="sm" onClick={() => navigate('/systems/edit/' + id)}><PlusSvg /></Button>
            </VinculoHeader>
            <VinculoChips>
              {activeAreas.map(area => (
                <VinculoChip key={area._id || area.name}>{area.name}</VinculoChip>
              ))}
            </VinculoChips>
          </VinculoCard>

          <VinculoCard>
            <VinculoHeader>
              <VinculoTitle>{t('mapping:systems.show.bonds.processes')}</VinculoTitle>
              <Button iconOnly variant="icon-filled" size="sm" onClick={() => navigate('/systems/edit/' + id)}><PlusSvg /></Button>
            </VinculoHeader>
            <VinculoChips>
              {activeProcesses.map(process => (
                <VinculoChip key={process._id || process.name}>{process.name}</VinculoChip>
              ))}
            </VinculoChips>
          </VinculoCard>

          <VinculoCard>
            <VinculoHeader>
              <VinculoTitle>{t('mapping:systems.show.bonds.assets')}</VinculoTitle>
              <Button iconOnly variant="icon-filled" size="sm" onClick={() => navigate('/systems/edit/' + id)}><PlusSvg /></Button>
            </VinculoHeader>
            <VinculoChips>
              {activeAssets.map(asset => (
                <VinculoChip key={asset._id || asset.name}>{asset.name}</VinculoChip>
              ))}
            </VinculoChips>
          </VinculoCard>
        </BondsGrid>
      )}
    </Container>
  );
};
